import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { WebsiteLayout } from '@/components/website/WebsiteLayout';
import { MapPin, Phone, ArrowLeft, Building2, CheckCircle, ArrowRight, FileText, Download, Shield, TrendingUp, Users, Star } from 'lucide-react';
import api from '@/utils/api';

export default function PropertyDetail() {
  const { id } = useParams();
  const [property, setProperty] = useState(null);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    api.get(`/public/properties/${id}`).then(r => setProperty(r.data)).catch(() => {});
    api.get('/public/website-settings').then(r => setSettings(r.data)).catch(() => {});
  }, [id]);

  if (!property) return (
    <WebsiteLayout>
      <div className="min-h-[60vh] flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-16 h-16 rounded-full border-4 border-[#DAA520] border-t-transparent animate-spin mx-auto mb-4" />
          <p className="text-slate-500">Loading property details...</p>
        </div>
      </div>
    </WebsiteLayout>
  );

  const amenities = (property.amenities || '').split(',').map(a => a.trim()).filter(Boolean);
  const highlights = (property.highlights || '').split(',').map(h => h.trim()).filter(Boolean);
  const hasMap = property.latitude && property.longitude;
  const hasGoogleUrl = property.google_maps_url;

  // Schema markup
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "RealEstateListing",
    "name": property.property_name,
    "description": property.description,
    "address": { "@type": "PostalAddress", "addressLocality": property.location },
    "offers": { "@type": "Offer", "price": property.total_price, "priceCurrency": "INR" }
  };

  return (
    <WebsiteLayout>
      <Helmet>
        <title>{`${property.property_name || 'Property'} | ${property.property_type || 'Property'} in ${property.location || 'Prime Location'} | Ganesa Homestate`}</title>
        <meta name="description" content={`${property.property_name || 'Property'} - ${property.property_type || 'Property'} in ${property.location || 'prime location'}. ${property.description || 'Premium property by Ganesa Homestate Developers.'} Book now!`} />
        <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
      </Helmet>

      {/* Breadcrumb */}
      <div className="bg-[#0A0A0F] py-4 border-b border-[#1A1A25]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center gap-2 text-sm">
          <Link to="/" className="text-slate-500 hover:text-[#DAA520] transition-colors">Home</Link>
          <span className="text-slate-600">/</span>
          <Link to="/properties" className="text-slate-500 hover:text-[#DAA520] transition-colors">Properties</Link>
          <span className="text-slate-600">/</span>
          <span className="text-[#DAA520]">{property.property_name}</span>
        </div>
      </div>

      {/* Property Hero */}
      <section className="bg-[#0A0A0F] pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-8">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div className="relative">
              <div className="h-72 sm:h-80 lg:h-[420px] bg-gradient-to-br from-[#1A1A25] to-[#0A0A0F] rounded-2xl flex items-center justify-center border border-[#1A1A25] overflow-hidden">
                <Building2 size={80} className="text-[#DAA520]/15" />
                <div className="absolute top-4 left-4 flex gap-2">
                  <span className="px-4 py-1.5 bg-[#DAA520] text-[#0A0A0F] text-xs font-bold rounded-full uppercase tracking-wider">{property.property_type || 'Property'}</span>
                  {property.status === 'upcoming' && <span className="px-4 py-1.5 bg-amber-500 text-white text-xs font-bold rounded-full">Upcoming</span>}
                </div>
                {property.rera_number && (
                  <div className="absolute bottom-4 left-4 flex items-center gap-1.5 bg-green-600/90 text-white text-xs font-bold px-3 py-1.5 rounded-full backdrop-blur">
                    <Shield size={12} /> RERA: {property.rera_number}
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-col justify-center">
              <div className="inline-flex items-center gap-2 text-[#DAA520] text-sm font-medium mb-3">
                <MapPin size={16} /> {property.location || 'Prime Location'}
                {property.address && <span className="text-slate-500 text-xs">| {property.address}</span>}
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight">{property.property_name}</h1>

              {property.description && (
                <p className="text-slate-400 leading-relaxed mb-6 text-base">{property.description}</p>
              )}

              {/* Quick Stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
                <div className="bg-[#1A1A25] rounded-xl p-4 text-center border border-[#1A1A25] hover:border-[#DAA520]/30 transition-colors">
                  <div className="text-[#DAA520] text-xl font-bold">{property.price_range || `₹${(property.total_price || 0).toLocaleString('en-IN')}`}</div>
                  <div className="text-xs text-slate-500 mt-1">Price</div>
                </div>
                {property.size && <div className="bg-[#1A1A25] rounded-xl p-4 text-center border border-[#1A1A25]"><div className="text-white text-xl font-bold">{property.size}</div><div className="text-xs text-slate-500 mt-1">Plot Size</div></div>}
                {property.total_area && <div className="bg-[#1A1A25] rounded-xl p-4 text-center border border-[#1A1A25]"><div className="text-white text-xl font-bold">{property.total_area}</div><div className="text-xs text-slate-500 mt-1">Total Area</div></div>}
                {property.total_units && <div className="bg-[#1A1A25] rounded-xl p-4 text-center border border-[#1A1A25]"><div className="text-white text-xl font-bold">{property.total_units}</div><div className="text-xs text-slate-500 mt-1">Units</div></div>}
              </div>

              <div className="flex gap-3">
                <Link to="/contact" className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-[#DAA520] to-[#B8860B] text-[#0A0A0F] font-bold rounded-xl hover:shadow-xl hover:shadow-[#DAA520]/25 transition-all hover:scale-[1.02] active:scale-[0.98]" data-testid="property-enquire-btn">
                  Enquire Now <ArrowRight size={18} />
                </Link>
                <a href={`tel:${settings?.phone}`} className="inline-flex items-center justify-center gap-2 px-6 py-4 border-2 border-[#DAA520]/40 text-[#DAA520] font-bold rounded-xl hover:bg-[#DAA520]/10 transition-all">
                  <Phone size={18} /> Call
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Details Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-3 gap-10">
            <div className="lg:col-span-2 space-y-10">
              {/* Highlights */}
              {highlights.length > 0 && (
                <div>
                  <h2 className="text-2xl font-bold text-[#0A0A0F] mb-6 flex items-center gap-2">
                    <Star size={22} className="text-[#DAA520]" /> Property Highlights
                  </h2>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {highlights.map((h, i) => (
                      <div key={i} className="flex items-center gap-3 bg-gradient-to-r from-slate-50 to-white rounded-xl p-4 border border-slate-100 hover:border-[#DAA520]/30 hover:shadow-sm transition-all">
                        <CheckCircle size={20} className="text-[#DAA520] shrink-0" />
                        <span className="text-sm font-medium text-[#0A0A0F]">{h}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Amenities */}
              {amenities.length > 0 && (
                <div>
                  <h2 className="text-2xl font-bold text-[#0A0A0F] mb-6">Amenities & Facilities</h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {amenities.map((a, i) => (
                      <div key={i} className="flex items-center gap-3 bg-white rounded-xl p-3.5 border border-slate-100 shadow-sm">
                        <div className="w-8 h-8 rounded-lg bg-[#DAA520]/10 flex items-center justify-center shrink-0">
                          <CheckCircle size={16} className="text-[#DAA520]" />
                        </div>
                        <span className="text-sm text-[#0A0A0F]">{a}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* PDF Maps & Documents */}
              {property.pdf_maps && property.pdf_maps.length > 0 && (
                <div data-testid="property-documents">
                  <h2 className="text-2xl font-bold text-[#0A0A0F] mb-6 flex items-center gap-2">
                    <FileText size={22} className="text-[#DAA520]" /> Documents & Maps
                  </h2>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {property.pdf_maps.map((pdf, i) => (
                      <a key={i} href={pdf.url} target="_blank" rel="noopener noreferrer"
                        className="group flex items-center gap-4 bg-white rounded-xl p-5 border border-slate-200 hover:border-[#DAA520] hover:shadow-lg transition-all">
                        <div className="w-14 h-14 rounded-xl bg-red-50 flex items-center justify-center shrink-0 group-hover:bg-red-100 transition-colors">
                          <FileText size={24} className="text-red-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-[#0A0A0F] truncate">{pdf.filename}</div>
                          <div className="text-xs text-slate-500 mt-0.5">Click to view/download</div>
                        </div>
                        <Download size={20} className="text-slate-400 group-hover:text-[#DAA520] transition-colors shrink-0" />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Google Maps */}
              {(hasMap || hasGoogleUrl) && (
                <div data-testid="property-map">
                  <h2 className="text-2xl font-bold text-[#0A0A0F] mb-6 flex items-center gap-2">
                    <MapPin size={22} className="text-[#DAA520]" /> Location Map
                  </h2>
                  <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-sm">
                    {hasGoogleUrl ? (
                      <iframe
                        src={property.google_maps_url}
                        width="100%" height="400" style={{ border: 0 }} allowFullScreen="" loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade" title="Property Location"
                      />
                    ) : hasMap ? (
                      <iframe
                        src={`https://maps.google.com/maps?q=${property.latitude},${property.longitude}&z=15&output=embed`}
                        width="100%" height="400" style={{ border: 0 }} allowFullScreen="" loading="lazy"
                        title="Property Location"
                      />
                    ) : null}
                  </div>
                  {property.address && (
                    <p className="mt-3 text-sm text-slate-500 flex items-center gap-2">
                      <MapPin size={14} className="text-[#DAA520]" /> {property.address}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-[#0A0A0F] to-[#142038] rounded-2xl p-6 sticky top-24 text-white">
                <h3 className="font-bold text-xl mb-2">Interested?</h3>
                <p className="text-sm text-slate-400 mb-6">Get pricing, brochure, and schedule a site visit.</p>
                <Link to="/contact" className="block w-full text-center px-6 py-4 bg-gradient-to-r from-[#DAA520] to-[#B8860B] text-[#0A0A0F] font-bold rounded-xl hover:shadow-xl hover:shadow-[#DAA520]/25 transition-all mb-3 hover:scale-[1.02] active:scale-[0.98]">
                  Book Site Visit
                </Link>
                <a href={`https://wa.me/${settings?.whatsapp}`} target="_blank" rel="noopener noreferrer"
                  className="block w-full text-center px-6 py-4 border border-green-500 text-green-400 font-bold rounded-xl hover:bg-green-500/10 transition-all mb-6">
                  WhatsApp Us
                </a>

                <div className="space-y-4 pt-4 border-t border-[#1A1A25]">
                  <h4 className="text-sm font-semibold text-slate-400 uppercase tracking-wider">Why Ganesa?</h4>
                  {[
                    { icon: Shield, text: 'RERA Approved Projects' },
                    { icon: TrendingUp, text: 'High Appreciation Value' },
                    { icon: Users, text: '1000+ Happy Families' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-[#DAA520]/10 flex items-center justify-center shrink-0">
                        <item.icon size={16} className="text-[#DAA520]" />
                      </div>
                      <span className="text-sm text-slate-300">{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </WebsiteLayout>
  );
}
